import { useState } from "react";
import { api, ApiError } from "../api.ts";
import { ErrorBanner } from "../ErrorBanner.tsx";
import { Modal } from "../Modal.tsx";
import {
  LISTING_CATEGORIES,
  type Listing,
  type ListingCategory,
} from "../types.ts";

interface Props {
  communityId: string;
  initial: Listing | null;
  onClose: () => void;
  onSaved: () => void;
}

interface CategoryFieldSpec {
  key: string;
  label: string;
  hint?: string;
  type?: "text" | "textarea";
}

const STRUCTURED_FIELDS: Record<ListingCategory, CategoryFieldSpec[]> = {
  transport: [
    { key: "departure_point", label: "Departure point" },
    { key: "destination", label: "Destination" },
    { key: "schedule", label: "Schedule", hint: "e.g. weekdays 07:00 / sun 09:00" },
    { key: "price", label: "Price" },
    { key: "contact", label: "Contact (WhatsApp / phone)" },
  ],
  delivery: [
    { key: "service_area", label: "Service area" },
    { key: "delivery_window", label: "Delivery window" },
    { key: "price", label: "Price / delivery fee" },
    { key: "contact", label: "Contact (WhatsApp / phone)" },
  ],
  events: [
    { key: "venue", label: "Venue" },
    { key: "date", label: "Date" },
    { key: "time", label: "Time" },
    { key: "price", label: "Price / ticket cost" },
    { key: "contact", label: "Contact" },
  ],
  where_to_buy: [
    { key: "shop_name", label: "Shop name" },
    { key: "address", label: "Address" },
    { key: "hours", label: "Hours" },
    { key: "what_to_buy", label: "What to buy", type: "textarea" },
    { key: "contact", label: "Contact" },
  ],
};

const CATEGORY_LABELS: Record<ListingCategory, string> = {
  transport: "Transport",
  delivery: "Delivery",
  events: "Events",
  where_to_buy: "Where to buy",
};

function structuredToStrings(
  s: Record<string, unknown>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(s ?? {})) {
    if (typeof v === "string") out[k] = v;
    else if (v == null) out[k] = "";
    else out[k] = String(v);
  }
  return out;
}

export function ListingForm({ communityId, initial, onClose, onSaved }: Props) {
  const isEdit = initial !== null;
  const [category, setCategory] = useState<ListingCategory>(
    initial?.category ?? "transport",
  );
  const [title, setTitle] = useState(initial?.title ?? "");
  const [body, setBody] = useState(initial?.body ?? "");
  const [structured, setStructured] = useState<Record<string, string>>(
    initial ? structuredToStrings(initial.structured) : {},
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function setField(key: string, value: string) {
    setStructured((prev) => ({ ...prev, [key]: value }));
  }

  function changeCategory(c: ListingCategory) {
    setCategory(c);
    setStructured({});
  }

  async function save() {
    setError(null);
    setBusy(true);
    try {
      const trimmedTitle = title.trim();
      if (!trimmedTitle) {
        setError("title_required");
        setBusy(false);
        return;
      }
      const structuredPayload: Record<string, string> = {};
      for (const [k, v] of Object.entries(structured)) {
        const trimmed = v.trim();
        if (trimmed) structuredPayload[k] = trimmed;
      }
      if (isEdit) {
        await api(`/admin/listings/${encodeURIComponent(initial!.id)}`, {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            title: trimmedTitle,
            body,
            structured: structuredPayload,
          }),
        });
      } else {
        await api(`/admin/listings`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            community_id: communityId,
            category,
            title: trimmedTitle,
            body,
            structured: structuredPayload,
          }),
        });
      }
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const fields = STRUCTURED_FIELDS[category];

  return (
    <Modal
      title={isEdit ? `Edit listing #${initial!.id}` : "Add listing"}
      subtitle={
        isEdit
          ? `community #${initial!.communityId} · ${CATEGORY_LABELS[initial!.category]}`
          : `community #${communityId}`
      }
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="luna-btn luna-btn-ghost"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="luna-btn luna-btn-primary"
            onClick={() => void save()}
            disabled={busy}
          >
            {busy
              ? isEdit
                ? "Saving…"
                : "Creating…"
              : isEdit
                ? "Save changes"
                : "Create listing"}
          </button>
        </>
      }
    >
      {error && <ErrorBanner code={error} />}
      <div className="field">
        <label htmlFor="l-category">Category</label>
        <select
          id="l-category"
          className="luna-input"
          value={category}
          onChange={(e) => changeCategory(e.target.value as ListingCategory)}
          disabled={isEdit}
        >
          {LISTING_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {CATEGORY_LABELS[c]}
            </option>
          ))}
        </select>
        {isEdit && (
          <span className="hint">
            Category is fixed after creation.
          </span>
        )}
      </div>
      <div className="field">
        <label htmlFor="l-title">Title</label>
        <input
          id="l-title"
          className="luna-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Short headline"
        />
      </div>
      <div className="field">
        <label htmlFor="l-body">Body</label>
        <textarea
          id="l-body"
          className="luna-input"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={4}
          placeholder="Description, details, anything that doesn't fit the structured fields below."
        />
      </div>
      <div className="field">
        <label>Structured fields ({CATEGORY_LABELS[category]})</label>
        {fields.map((f) =>
          f.type === "textarea" ? (
            <div key={f.key} className="field" style={{ marginTop: 8 }}>
              <label htmlFor={`l-s-${f.key}`}>{f.label}</label>
              <textarea
                id={`l-s-${f.key}`}
                className="luna-input"
                value={structured[f.key] ?? ""}
                onChange={(e) => setField(f.key, e.target.value)}
                rows={3}
              />
              {f.hint && <span className="hint">{f.hint}</span>}
            </div>
          ) : (
            <div key={f.key} className="field" style={{ marginTop: 8 }}>
              <label htmlFor={`l-s-${f.key}`}>{f.label}</label>
              <input
                id={`l-s-${f.key}`}
                className="luna-input"
                value={structured[f.key] ?? ""}
                onChange={(e) => setField(f.key, e.target.value)}
              />
              {f.hint && <span className="hint">{f.hint}</span>}
            </div>
          ),
        )}
      </div>
    </Modal>
  );
}
